"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LoyaltyEventUploadResponse = exports.LoyaltyEventAck = exports.LoyaltyEventRecord = exports.LoyaltyMemberLookup = exports.LoyaltyReceiptFinalizeResponse = exports.LoyaltyReceiptCalcResponse = exports.LoyaltyReceiptFinalizeRequest = exports.LoyaltyReceiptCalcRequest = exports.LoyaltyReceiptCustomerRef = exports.LoyaltyReceiptLine = exports.LOYALTY_RELAY_AUTH_EXPIRED = exports.LOYALTY_RELAY_TIMEOUT = exports.LOYALTY_RELAY_UNREACHABLE = exports.LoyaltyRelayErrorBody = exports.EnrolLoyaltyMemberPos = exports.LoyaltyCardSource = exports.LoyaltyTenantConfig = exports.LoyaltyEnrolmentField = exports.PatchLoyaltyMember = exports.EnrolLoyaltyMember = exports.LoyaltyMemberAdmin = exports.LoyaltyEventKind = exports.LoyaltyPhone = exports.LoyaltyMemberNumber = exports.LoyaltyMemberStatus = void 0;
const zod_1 = require("zod");
/**
 * Loyalty contracts (RFL-136).
 *
 * The loyalty programme is authored on the Backoffice; terminals
 * (POS + SCO) read it through an **always-online lookup** (by card
 * number or phone) and push earn/redeem events back up via an
 * idempotent upload. Member balances are NOT synced down — they
 * change server-side (cross-terminal accrual, web channel, support
 * adjustments) and a stale local copy would mis-state points at the
 * till. The lookup is therefore `requires_online`; only the event
 * upload participates in the offline outbox.
 *
 * Enables the loyalty-entry sub-task of the SCO/POS work (RFL-31):
 * terminals can show "enter card/phone → see balance" and attach an
 * accrual/redemption to a finalised transaction.
 */
// ---------- Primitive shapes ----------
exports.LoyaltyMemberStatus = zod_1.z.enum(['active', 'suspended']);
/**
 * Loyalty card / membership number — the human-facing identifier a
 * cashier or shopper types at the till. Distinct from the cuid `id`.
 */
exports.LoyaltyMemberNumber = zod_1.z
    .string()
    .min(3)
    .max(64)
    .regex(/^[A-Za-z0-9_-]+$/, 'alphanumeric / underscore / dash only');
/** Loose phone shape — normalisation/validation is a Phase 2 concern. */
exports.LoyaltyPhone = zod_1.z
    .string()
    .min(5)
    .max(32)
    .regex(/^\+?[0-9 ()-]+$/, 'digits, spaces, +, (), - only');
exports.LoyaltyEventKind = zod_1.z.enum(['accrual', 'redemption', 'adjustment']);
// ---------- Admin REST surface ----------
/** Full admin shape returned by the Backoffice member directory. */
exports.LoyaltyMemberAdmin = zod_1.z.object({
    id: zod_1.z.string(),
    memberNumber: exports.LoyaltyMemberNumber,
    phone: exports.LoyaltyPhone.nullable(),
    email: zod_1.z.string().email().nullable(),
    displayName: zod_1.z.string().min(1).max(200),
    pointsBalance: zod_1.z.number().int(),
    status: exports.LoyaltyMemberStatus,
    enrolledAt: zod_1.z.string().datetime(),
    createdAt: zod_1.z.string().datetime(),
    updatedAt: zod_1.z.string().datetime(),
});
exports.EnrolLoyaltyMember = zod_1.z.object({
    memberNumber: exports.LoyaltyMemberNumber,
    displayName: zod_1.z.string().min(1).max(200),
    phone: exports.LoyaltyPhone.optional(),
    email: zod_1.z.string().email().optional(),
    /** Opening balance (e.g. migrated from a legacy programme). */
    initialPoints: zod_1.z.number().int().min(0).max(10_000_000).default(0),
});
exports.PatchLoyaltyMember = zod_1.z
    .object({
    displayName: zod_1.z.string().min(1).max(200).optional(),
    status: exports.LoyaltyMemberStatus.optional(),
    phone: exports.LoyaltyPhone.nullable().optional(),
    email: zod_1.z.string().email().nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'at least one field is required' });
// ---------- Terminal-facing config + enrolment (delegated to re:form Loyalty) ----------
/**
 * One retailer-configurable enrolment-form field. The terminal renders the
 * `visible` fields, marks `required` ones, and labels them with `label`; the
 * collected values are submitted back under `fields[key]` on enrolment.
 */
exports.LoyaltyEnrolmentField = zod_1.z.object({
    key: zod_1.z.string().min(1).max(64),
    label: zod_1.z.string().min(1).max(120),
    required: zod_1.z.boolean(),
    visible: zod_1.z.boolean(),
});
/**
 * Per-tenant POS-loyalty config the terminal reads on startup: whether to offer
 * enrolment on an unknown card, and which fields the enrolment form collects.
 * Sourced from re:form Loyalty when delegation is on.
 */
exports.LoyaltyTenantConfig = zod_1.z.object({
    offerEnrolment: zod_1.z.boolean(),
    enrolmentFields: zod_1.z.array(exports.LoyaltyEnrolmentField),
});
/**
 * Where the enrolled member's card comes from: a number swiped/typed at the
 * till (register it), a freshly generated one, or a pre-printed stock card.
 * `number` is required for `swiped` and `stock`.
 */
exports.LoyaltyCardSource = zod_1.z.enum(['swiped', 'generate', 'stock']);
/**
 * Terminal-facing enrolment request. `fields` carries the retailer-configurable
 * values (see `LoyaltyTenantConfig.enrolmentFields`); `fullName` is always
 * required by the loyalty engine. Returns a `LoyaltyMemberLookup`.
 */
exports.EnrolLoyaltyMemberPos = zod_1.z
    .object({
    fields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()),
    card: zod_1.z
        .object({
        mode: exports.LoyaltyCardSource,
        number: exports.LoyaltyMemberNumber.optional(),
    })
        .strict(),
    /** ISO country for the new customer/card (defaults server-side). */
    country: zod_1.z.string().min(2).max(3).optional(),
})
    .strict()
    .refine((v) => v.card.mode === 'generate' || v.card.number !== undefined, {
    message: 'card.number is required for swiped and stock modes',
    path: ['card', 'number'],
});
/**
 * RL-564 — what a till gets from Back Office's Loyalty relay
 * (`GET /loyalty/lookup`, `GET /loyalty/config`, `POST /loyalty/enrol`) when
 * the tenant is loyalty-delegated and the call does not succeed.
 *
 * - **Loyalty refused (4xx):** the same HTTP status, and Loyalty's own `code`
 *   and `message` unchanged — e.g. 409 `legacy_ac_enrolment_blocked`,
 *   409 `legacy_ac_enrolment_other_target`, 409 `already_enrolled`,
 *   409 `card_already_issued`, 409 `legacy_ac_enrolment_in_progress`.
 *   A 404 on lookup is "no such member".
 * - **Loyalty failed with a code (5xx):** 503 with that `code`, e.g.
 *   `legacy_ac_enrolment_not_configured`. A code-less 5xx is a plain 503.
 * - **The relay itself:** 503 `loyalty_relay_unreachable` (Loyalty could not
 *   be reached), 504 `loyalty_relay_timeout` (no answer within
 *   `LOYALTY_DELEGATION_TIMEOUT_MS`, default 15 s), 503
 *   `loyalty_relay_auth_expired` (an enrolment without an `Idempotency-Key` met
 *   an expired service token; it was not sent again).
 *
 * `POST /loyalty/enrol` forwards the till's `Idempotency-Key` header to Loyalty
 * unchanged (1-255 printable characters, no spaces; anything else is 400), so
 * a retried enrolment resumes the one already started. Without the header the
 * enrolment is sent once and never re-sent by Back Office.
 */
exports.LoyaltyRelayErrorBody = zod_1.z.object({
    statusCode: zod_1.z.number().int(),
    message: zod_1.z.union([zod_1.z.string(), zod_1.z.array(zod_1.z.string())]),
    /** Loyalty's code, passed through, or one of the `LOYALTY_RELAY_*` codes. */
    code: zod_1.z.string().min(1).optional(),
});
exports.LOYALTY_RELAY_UNREACHABLE = 'loyalty_relay_unreachable';
exports.LOYALTY_RELAY_TIMEOUT = 'loyalty_relay_timeout';
exports.LOYALTY_RELAY_AUTH_EXPIRED = 'loyalty_relay_auth_expired';
// ---------- Terminal-facing receipt pricing (delegated; degrade-to-base when down) ----------
/** One basket line sent to loyalty for pricing. `unitPrice` is in minor units. */
exports.LoyaltyReceiptLine = zod_1.z.object({
    lineId: zod_1.z.string().min(1),
    productId: zod_1.z.string().min(1),
    productGroupId: zod_1.z.string().optional(),
    unitPrice: zod_1.z.number().int().min(0),
    quantity: zod_1.z.number().int().min(1),
});
/** The member context for pricing (resolved from a prior lookup). */
exports.LoyaltyReceiptCustomerRef = zod_1.z.object({
    id: zod_1.z.string().optional(),
    segments: zod_1.z.array(zod_1.z.string()).optional(),
    cardCategory: zod_1.z.string().optional(),
});
/** Basket sent to loyalty for real-time pricing. */
exports.LoyaltyReceiptCalcRequest = zod_1.z.object({
    currency: zod_1.z.string().length(3),
    storeId: zod_1.z.string().optional(),
    registerId: zod_1.z.string().optional(),
    country: zod_1.z.string().optional(),
    channel: zod_1.z.string().optional(),
    cardId: zod_1.z.string().optional(),
    customer: exports.LoyaltyReceiptCustomerRef.optional(),
    lines: zod_1.z.array(exports.LoyaltyReceiptLine).min(1),
    mode: zod_1.z.enum(['best_benefit', 'priority']).optional(),
    /** Redemption is ONLINE-ONLY; ignored on a degraded (offline) response. */
    bonusRedeem: zod_1.z.number().int().min(0).optional(),
    certificateRedeem: zod_1.z.number().int().min(0).optional(),
});
/** Finalize request: the basket plus the posting references. */
exports.LoyaltyReceiptFinalizeRequest = exports.LoyaltyReceiptCalcRequest.extend({
    reference: zod_1.z.string().optional(),
    bonusAccountId: zod_1.z.string().optional(),
    certificateId: zod_1.z.string().optional(),
    certificatePin: zod_1.z.string().optional(),
    promoCodeUse: zod_1.z.string().optional(),
});
/**
 * Degrade-aware pricing response. When loyalty is reachable, `pricing` carries
 * the engine result verbatim (recalculated lines/totals/discounts/accruals +
 * `messages` display/receipt texts + coupons — the loyalty engine owns the exact
 * shape). When NOT reachable, `loyaltyAvailable=false` + a `message` and the till
 * rings the basket at its base prices.
 */
exports.LoyaltyReceiptCalcResponse = zod_1.z.object({
    loyaltyAvailable: zod_1.z.boolean(),
    message: zod_1.z.string().optional(),
    pricing: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
});
/** Degrade-aware finalize response; `receipt` is the persisted loyalty receipt when available. */
exports.LoyaltyReceiptFinalizeResponse = zod_1.z.object({
    loyaltyAvailable: zod_1.z.boolean(),
    message: zod_1.z.string().optional(),
    receipt: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
});
// ---------- Terminal-facing lookup ----------
/**
 * Minimal-PII shape returned to terminals on lookup. Deliberately
 * narrower than the admin shape — no email, no audit timestamps —
 * since it crosses to the till surface.
 */
exports.LoyaltyMemberLookup = zod_1.z.object({
    id: zod_1.z.string(),
    memberNumber: exports.LoyaltyMemberNumber,
    displayName: zod_1.z.string(),
    pointsBalance: zod_1.z.number().int(),
    status: exports.LoyaltyMemberStatus,
    /** Loyalty-engine segments used for terminal treatment such as VIP notices. */
    segments: zod_1.z.array(zod_1.z.string().min(1).max(64)).max(64).optional(),
});
// ---------- Terminal event upload ----------
/**
 * A single earn/redeem/adjustment event. `points` is a positive
 * magnitude; the server applies the sign by `kind` (accrual /
 * adjustment add, redemption subtracts) so terminals never have to
 * reason about signs. Idempotent on `(terminalId, clientUuid)`.
 */
exports.LoyaltyEventRecord = zod_1.z.object({
    clientUuid: zod_1.z.string().min(8).max(64),
    memberId: zod_1.z.string(),
    kind: exports.LoyaltyEventKind,
    points: zod_1.z.number().int().min(1).max(10_000_000),
    receiptClientUuid: zod_1.z.string().min(8).max(64).nullable().default(null),
    occurredAt: zod_1.z.string().datetime(),
    note: zod_1.z.string().max(500).optional(),
});
/** Per-record acknowledgement (mirrors the sync upload ack shape). */
exports.LoyaltyEventAck = zod_1.z.object({
    clientUuid: zod_1.z.string(),
    accepted: zod_1.z.boolean(),
    serverId: zod_1.z.string().optional(),
    /** New balance after the event was applied (present when accepted). */
    resultingBalance: zod_1.z.number().int().optional(),
    retriable: zod_1.z.boolean().optional(),
    code: zod_1.z.string().optional(),
    message: zod_1.z.string().optional(),
});
exports.LoyaltyEventUploadResponse = zod_1.z.object({
    acks: zod_1.z.array(exports.LoyaltyEventAck),
    accepted: zod_1.z.number().int(),
    rejected: zod_1.z.number().int(),
});
//# sourceMappingURL=loyalty.js.map